import React from 'react';

/**
 * An off-screen, plain-language version of the current recommendation,
 * positioned so screen readers can announce it AND so AI crawlers that
 * read `document.body.innerText` walk away with the actual answer rather
 * than just form labels.
 *
 * Visually hidden via `clip` + offscreen positioning (the standard "visually
 * hidden but still in the accessibility tree" recipe) — NOT
 * `display:none`, which would also hide it from screen readers and most
 * crawlers.
 */
const visuallyHiddenStyle = {
  position: 'absolute',
  width: '1px',
  height: '1px',
  padding: 0,
  margin: '-1px',
  overflow: 'hidden',
  clip: 'rect(0,0,0,0)',
  whiteSpace: 'nowrap',
  border: 0
};

export const todayAnswerText = ({ city, displayTemp, recommendation, forecastHours }) => {
  if (!recommendation || !city) return null;
  const whenLabel = forecastHours === 0 ? 'right now'
    : forecastHours === -24 ? 'yesterday'
    : `in ${forecastHours} hours`;
  const tempPart = displayTemp != null ? `The temperature is ${Math.round(displayTemp)}°C. ` : '';
  return `In ${city} ${whenLabel}, ${tempPart}layers.today recommends ${recommendation.layers} layer${recommendation.layers === 1 ? '' : 's'} — ${recommendation.description}. See https://layers.today/?city=${encodeURIComponent(city)}${forecastHours === 0 ? '' : `&when=${forecastHours === -24 ? 'yesterday' : forecastHours + 'h'}`} for the live answer.`;
};

export const tripAnswerText = ({ data, homeName }) => {
  if (!Array.isArray(data) || !data.length) return null;
  const lines = data.map(({ stop, days }) => {
    if (!days || !days.length) return null;
    const lows = days.map((d) => d.temperature?.low).filter((v) => typeof v === 'number');
    const highs = days.map((d) => d.temperature?.high).filter((v) => typeof v === 'number');
    const counts = days.map((d) => d.recommendation?.layers).filter((n) => n != null);
    if (!counts.length) return null;
    const mode = [...counts].sort((a, b) =>
      counts.filter((v) => v === a).length - counts.filter((v) => v === b).length
    ).pop();
    const low = lows.length ? Math.round(Math.min(...lows)) : null;
    const high = highs.length ? Math.round(Math.max(...highs)) : null;
    return `In ${stop.name}: ${low != null && high != null ? `${low}°–${high}°C, ` : ''}${mode} layer${mode === 1 ? '' : 's'} most days.`;
  }).filter(Boolean);
  if (!lines.length) return null;
  return `Trip plan from layers.today. ${lines.join(' ')}${homeName ? ` Returning home to ${homeName}.` : ''}`;
};

const LlmAnswer = ({ children }) => {
  if (!children) return null;
  return (
    <section data-llm-answer aria-label="Plain-language summary" style={visuallyHiddenStyle}>
      {children}
    </section>
  );
};

export default LlmAnswer;
